import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { AppContext } from '../../../context/app-context';
import { formatCurrencyValue, parseCurrencyValue } from '../../../util/PaymentUtils';
import EquipmentTypes from '../../../components/EquipmentTypes';
import { LocationEquipmentTypeData, LocationWizardData, newEquipmentRowKey } from '../types';
import { StepErrors } from '../stepValidation';
import WizardRequiredNote from '../components/WizardRequiredNote';
import { toClampedNumber } from '../utility';
import { getManagedSpacesDefault } from '../locationUpdateHelpers';
import {
  defaultEquipmentOverageRate,
  getEquipmentOverageRateError,
  getEquipmentPriceError,
  hasEquipmentOverageRate,
  positiveOrNull,
} from '../equipmentOverageRate';

interface LocationPricingStepProps {
  data: LocationWizardData;
  updateData: (partial: Partial<LocationWizardData>) => void;
  errors?: StepErrors;
}

const EQUIPMENT_OPTIONS = EquipmentTypes.OPTIONS;

/** Default SecurSpace fee percentage used for payout estimates. */
const DEFAULT_FEE_PERCENTAGE = 15;

/**
 * Estimate the supplier payout for a given price (in cents).
 * @param priceInCents Price in cents; returns '$0' when null or non-positive.
 * @param feePercentage SecurSpace fee percentage to deduct (e.g. 15 for 15%).
 */
export const estimatePayout = (priceInCents: number | null, feePercentage: number): string => {
  if (!priceInCents || priceInCents <= 0) return '$0';
  const payout = Math.round(priceInCents * (1 - feePercentage / 100));
  return formatCurrencyValue(payout) as string || '$0';
};

/** Format a currency value for display, defaulting to '$0' when unset. */
export const formatDisplay = (value: number | null): string => {
  if (!value || value <= 0) return '$0';
  return formatCurrencyValue(value) as string || '$0';
};

/**
 * Display value for an equipment row's Price/day cell. Returns 'N/A' when daily
 * bookings are disabled so suppliers don't see misleading daily rates; otherwise
 * shows the per-type override or the location default rate. A $0 override is
 * treated as blank, matching how the API prices bookings.
 */
export const equipmentDayPriceDisplay = (
  allowDailyBookings: boolean,
  equipmentPricePerDay: number | null,
  defaultPricePerDay: number | null
): string => {
  if (!allowDailyBookings) return 'N/A';
  if (positiveOrNull(equipmentPricePerDay) !== null) return formatDisplay(equipmentPricePerDay);
  return `${formatDisplay(defaultPricePerDay)} (default)`;
};

/**
 * Display value for an equipment row's Price/month cell: the per-type override
 * when set, otherwise the location default rate. A $0 override is treated as blank.
 */
export const equipmentMonthPriceDisplay = (
  equipmentPricePerMonth: number | null,
  defaultPricePerMonth: number | null
): string => {
  if (positiveOrNull(equipmentPricePerMonth) !== null) return formatDisplay(equipmentPricePerMonth);
  return `${formatDisplay(defaultPricePerMonth)} (default)`;
};

/**
 * Display value for an equipment row's Price/Day Payout (est.) cell. Returns
 * 'N/A' when daily bookings are disabled; otherwise the estimated payout.
 */
export const equipmentDayPayoutDisplay = (
  allowDailyBookings: boolean,
  effectiveDayPrice: number | null,
  feePercentage: number
): string => {
  if (!allowDailyBookings) return 'N/A';
  return estimatePayout(effectiveDayPrice, feePercentage);
};

/**
 * Display value for an equipment row's Overage rate cell: the per-type rate when
 * set, otherwise the rate it falls back to, marked "(default)".
 */
export const equipmentOverageRateDisplay = (
  equipmentOverageRate: number | null | undefined,
  defaultOverageRate: number
): string => {
  if (hasEquipmentOverageRate(equipmentOverageRate)) return formatDisplay(equipmentOverageRate);
  return `${formatDisplay(defaultOverageRate)} (default)`;
};

/**
 * Equipment table cell showing a price with its estimated payout beneath it.
 * Omit `payout` (e.g. N/A daily pricing) to show the price alone.
 */
const PriceWithPayout = ({ price, payout }: { price: string; payout?: string | null }) => (
  <>
    <Typography variant="body2">{price}</Typography>
    {payout != null && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
        Payout: {payout} (est.)
      </Typography>
    )}
  </>
);

/**
 * Step 5 (Pricing) of the Create/Edit Location wizard.
 *
 * Captures location-level pricing (monthly, daily, overage) and equipment type
 * pricing. Admin-only settings (charge overages, QR scanner, live toggle) live
 * exclusively in the separate admin-only Step 6 (Admin Settings).
 */
const LocationPricingStep = (props: LocationPricingStepProps) => {
  const { data, updateData, errors } = props;
  const fieldErrors = errors || {};
  const { user: account } = useContext(AppContext);
  const feePercentage = account?.feePercentage ?? DEFAULT_FEE_PERCENTAGE;

  // "Yes" is represented by a non-null pricePerDay (0 is a valid "enabled" placeholder);
  // "No" is represented by null.
  const allowDailyBookings = data.pricePerDay !== null;

  // Daily bookings are mandatory when automated approvals are enabled, so "No" is
  // locked out. Disabling the radio alone doesn't move the selection to "Yes",
  // which would leave a disabled "No" as the only checked option. Force the toggle
  // on whenever auto-approve is enabled but no daily price is set yet. This covers
  // edit mode and enabling auto-approve after the first visit to this step, both of
  // which the step-entry default in the hook intentionally skips.
  useEffect(() => {
    if (data.managedLocation && data.pricePerDay === null) {
      updateData({ pricePerDay: 0 });
    }
  }, [data.managedLocation, data.pricePerDay, updateData]);

  const handleAllowDailyToggle = (allow: boolean) => {
    if (!allow) {
      updateData({ pricePerDay: null });
    } else {
      // Set a placeholder so the toggle is "on"; the user will fill in the actual value.
      updateData({ pricePerDay: data.pricePerDay && data.pricePerDay > 0 ? data.pricePerDay : 0 });
    }
  };

  const handleCurrencyChange = (field: 'pricePerDay' | 'pricePerMonth' | 'overageRate', rawValue: string) => {
    const parsed = parseCurrencyValue(rawValue);
    let value = typeof parsed === 'number' && !isNaN(parsed) ? parsed : null;
    // While daily bookings are enabled, keep pricePerDay non-null (0 when cleared)
    // so clearing the field doesn't silently switch the toggle back to "No".
    if (field === 'pricePerDay' && value === null) {
      value = 0;
    }
    updateData({ [field]: value });
  };

  // --- Equipment type helpers ---
  const equipmentRows = data.locationEquipmentTypes || [];

  const removeEquipmentRowAt = (index: number) => {
    updateData({
      locationEquipmentTypes: equipmentRows.filter((_, i) => i !== index),
    });
  };

  const selectedTypes = new Set(
    equipmentRows.map((e) => e.equipmentType).filter(Boolean)
  );

  const availableToAdd = EQUIPMENT_OPTIONS.filter((opt) => !selectedTypes.has(opt));

  /**
   * Format a currency value for an equipment price input. Returns an empty
   * string when unset so the default-rate placeholder can show through.
   */
  const formatEquipmentInput = (value: number | null): string => {
    if (value === null || value === undefined) return '';
    return formatCurrencyValue(value) as string || '';
  };

  /** Placeholder text showing the default rate that an empty override inherits. */
  const defaultPlaceholder = (defaultValue: number | null): string => {
    const formatted = formatDisplay(defaultValue);
    return `${formatted} (default)`;
  };

  // --- Equipment modal state ---
  interface EquipmentModalData {
    open: boolean;
    editIndex: number | null;
    equipmentType: string;
    pricePerDay: string;
    pricePerMonth: string;
    overageRate: string;
  }

  const [modal, setModal] = useState<EquipmentModalData>({
    open: false,
    editIndex: null,
    equipmentType: '',
    pricePerDay: '',
    pricePerMonth: '',
    overageRate: '',
  });

  const openAddModal = () => {
    setModal({ open: true, editIndex: null, equipmentType: '', pricePerDay: '', pricePerMonth: '', overageRate: '' });
  };

  const openEditModal = (index: number) => {
    const eq = equipmentRows[index];
    setModal({
      open: true,
      editIndex: index,
      equipmentType: eq.equipmentType,
      pricePerDay: formatEquipmentInput(eq.pricePerDay),
      pricePerMonth: formatEquipmentInput(eq.pricePerMonth),
      overageRate: formatEquipmentInput(eq.overageRate ?? null),
    });
  };

  const handleModalClose = () => {
    setModal((prev) => ({ ...prev, open: false }));
  };

  /**
   * Format an equipment price field as the supplier types, matching the
   * default-pricing fields. Currency values are stored in cents, so an unformatted
   * entry like "15" parses to 15 cents instead of $15.00 — reformatting on every
   * keystroke keeps the decimal point positioned correctly on the initial add
   * (the edit modal already pre-fills a formatted value).
   */
  const handleModalCurrencyChange = (field: 'pricePerDay' | 'pricePerMonth' | 'overageRate', rawValue: string) => {
    if (rawValue.trim() === '') {
      setModal((prev) => ({ ...prev, [field]: '' }));
      return;
    }
    const formatted = formatCurrencyValue(parseCurrencyValue(rawValue)) as string;
    setModal((prev) => ({ ...prev, [field]: formatted }));
  };

  const parseModalPrice = (value: string): number | null =>
    value.trim() === '' ? null : parseCurrencyValue(value);

  const rawModalDay = parseModalPrice(modal.pricePerDay);
  const rawModalMonth = parseModalPrice(modal.pricePerMonth);
  // Validate before normalizing so invalid input is blocked rather than silently
  // replaced by the default. The daily field is hidden when daily bookings are off.
  const modalDayError = allowDailyBookings ? getEquipmentPriceError(rawModalDay, 'daily price') : null;
  const modalMonthError = getEquipmentPriceError(rawModalMonth, 'monthly price');
  // $0 means "not set" to the API, so store it as blank to show the default.
  const modalPrices = {
    pricePerDay: positiveOrNull(rawModalDay),
    pricePerMonth: positiveOrNull(rawModalMonth),
  };
  const parsedModalOverage = parseModalPrice(modal.overageRate);
  const modalOverageError = getEquipmentOverageRateError(parsedModalOverage);
  const hasModalError = Boolean(modalDayError || modalMonthError || modalOverageError);

  const handleModalSave = () => {
    if (hasModalError) return;
    const { pricePerDay: parsedDay, pricePerMonth: parsedMonth } = modalPrices;
    const parsedOverage = hasEquipmentOverageRate(parsedModalOverage) ? parsedModalOverage : null;

    if (modal.editIndex !== null) {
      // Edit existing row
      const updated = equipmentRows.map((e, i) =>
        i === modal.editIndex
          ? {
            ...e,
            equipmentType: modal.equipmentType,
            pricePerDay: parsedDay,
            pricePerMonth: parsedMonth,
            overageRate: parsedOverage,
          }
          : e
      );
      updateData({ locationEquipmentTypes: updated });
    } else {
      // Add new row
      const newEntry: LocationEquipmentTypeData = {
        _key: newEquipmentRowKey(),
        equipmentType: modal.equipmentType,
        pricePerDay: parsedDay,
        pricePerWeek: null,
        pricePerMonth: parsedMonth,
        overageRate: parsedOverage,
      };
      updateData({ locationEquipmentTypes: [...equipmentRows, newEntry] });
    }
    handleModalClose();
  };

  /** Equipment types available in the modal dropdown (excludes types already used by other rows). */
  const modalAvailableTypes = EQUIPMENT_OPTIONS.filter((opt) => {
    const usedByOthers = new Set(
      equipmentRows
        .filter((_, i) => i !== modal.editIndex)
        .map((e) => e.equipmentType)
        .filter(Boolean)
    );
    return !usedByOthers.has(opt);
  });

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Location Pricing
      </Typography>
      <WizardRequiredNote />

      {/* Allow Daily Bookings */}
      <FormControl sx={{ display: 'block', mb: 1 }}>
        <FormLabel id="allow-daily-bookings-label">Allow Daily Bookings</FormLabel>
        <RadioGroup
          row
          aria-labelledby="allow-daily-bookings-label"
          value={allowDailyBookings ? 'yes' : 'no'}
          onChange={(e) => handleAllowDailyToggle(e.target.value === 'yes')}
        >
          {/* Daily bookings are mandatory when automated approvals are enabled,
              so "No" is locked out to prevent an invalid configuration. */}
          <FormControlLabel
            value="no"
            control={<Radio />}
            label="No"
            disabled={data.managedLocation}
          />
          <FormControlLabel value="yes" control={<Radio />} label="Yes" />
        </RadioGroup>
      </FormControl>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 3 }}>
        {data.managedLocation
          ? 'Daily pricing is required because automated approvals are enabled for this location.'
          : 'Daily pricing allows buyers to book spaces by the day. Required when automated approvals are enabled.'}
      </Typography>

      {allowDailyBookings && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.managedLocation}
                onChange={(e) => {
                  const managedLocation = e.target.checked;
                  updateData({
                    managedLocation,
                    managedSpaces: getManagedSpacesDefault(managedLocation),
                  });
                }}
              />
            }
            label={
              <span>
                <strong>Allow automated approvals of space reservations.</strong>{' '}
                <em style={{ fontSize: '0.85em' }}>(Only available for daily bookings.)</em>
              </span>
            }
            sx={{ alignItems: 'center' }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, fontWeight: 700 }}>
            Locations with automated approvals get 3x more bookings on average than those requiring
            approval.
          </Typography>

          {data.managedLocation && (
            <>
              <TextField
                label="Number of Auto-Approve Spaces"
                type="number"
                required
                fullWidth
                inputProps={{ min: 1 }}
                value={data.managedSpaces ?? ''}
                onChange={(e) => updateData({ managedSpaces: toClampedNumber(e.currentTarget, 1) })}
                error={Boolean(fieldErrors.managedSpaces)}
                helperText={
                  fieldErrors.managedSpaces ||
                  'The number of spaces that can be automatically approved without manual review.'
                }
                sx={{ maxWidth: { sm: '50%' }, mt: 3 }}
              />
              <Typography variant="body2" color="error" sx={{ ml: 4, fontWeight: 700, mb: 2 }}>
                If you're using SecurSpace GMS, instant approval will automatically turn off once your location reaches full capacity.
              </Typography>
            </>
          )}
        </>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Default (location-wide) pricing */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        Default Pricing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        These rates apply to every booking at this location unless you set a specific price for an
        equipment type below.
      </Typography>

      {/* Price Per Day (only when daily bookings are enabled) */}
      {allowDailyBookings && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="Price Per Day"
              required
              fullWidth
              value={formatDisplay(data.pricePerDay)}
              onChange={(e) => handleCurrencyChange('pricePerDay', e.target.value)}
              placeholder="$0"
              error={Boolean(fieldErrors.pricePerDay)}
              helperText={fieldErrors.pricePerDay || 'Amount Per Space'}
            />
            <TextField
              label="Your Payout Per Space (estimate)"
              fullWidth
              value={estimatePayout(data.pricePerDay, feePercentage)}
              InputProps={{ readOnly: true }}
              inputProps={{ tabIndex: -1 }}
            />
          </Box>

          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {/* Price Per Month */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 3,
          mb: 3,
        }}
      >
        <TextField
          label="Price Per Month"
          fullWidth
          value={formatDisplay(data.pricePerMonth)}
          onChange={(e) => handleCurrencyChange('pricePerMonth', e.target.value)}
          placeholder="$0"
          error={Boolean(fieldErrors.pricePerMonth)}
          helperText={
            fieldErrors.pricePerMonth ||
            'Amount Per Space. Leave as $0 to apply the daily rate to monthly bookings.'
          }
        />
        <TextField
          label="Your Payout Per Space (estimate)"
          fullWidth
          value={estimatePayout(data.pricePerMonth, feePercentage)}
          InputProps={{ readOnly: true }}
          inputProps={{ tabIndex: -1 }}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Overage Rate */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 3,
          mb: 1,
        }}
      >
        <TextField
          label="Overage Rate"
          fullWidth
          value={formatDisplay(data.overageRate)}
          onChange={(e) => handleCurrencyChange('overageRate', e.target.value)}
          placeholder="$0"
          helperText="Amount Per Space"
        />
        <TextField
          label="Your Payout Per Space (estimate)"
          fullWidth
          value={estimatePayout(data.overageRate, feePercentage)}
          InputProps={{ readOnly: true }}
          inputProps={{ tabIndex: -1 }}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <strong>Overage rates</strong> apply immediately to daily bookings. For recurring bookings,
        updates will take effect in the next billing cycle.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        <strong>Note:</strong> Overage rates must be enabled by SecurSpace. They&apos;ll notify you once this is set up
        for your location.
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Equipment types accepted + optional per-type price overrides */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Accepted Equipment Types
          <Box component="span" sx={{ color: 'error.main' }}>
            *
          </Box>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mb: 2 }}>
          Select which equipment types your yard accepts. Leave a price blank to use your default
          rate, or enter a price to charge that equipment type differently.
        </Typography>
        {fieldErrors.locationEquipmentTypes && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {fieldErrors.locationEquipmentTypes}
          </Typography>
        )}
        {equipmentRows.length > 0 && (
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Equipment Type</TableCell>
                  <TableCell>Price/day</TableCell>
                  <TableCell>Price/month</TableCell>
                  <TableCell>Overage rate</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {equipmentRows.map((eq, index) => {
                  // Effective price = per-type override if set, otherwise the default.
                  const effectiveDay = positiveOrNull(eq.pricePerDay) ?? data.pricePerDay;
                  const effectiveMonth = positiveOrNull(eq.pricePerMonth) ?? data.pricePerMonth;
                  const defaultOverage = defaultEquipmentOverageRate(eq, data);
                  const effectiveOverage = hasEquipmentOverageRate(eq.overageRate) ? eq.overageRate : defaultOverage;
                  return (
                    <TableRow key={eq._key ?? eq.id ?? index}>
                      <TableCell>
                        <Typography variant="body2">
                          {eq.equipmentType || <em>Not selected</em>}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <PriceWithPayout
                          price={equipmentDayPriceDisplay(allowDailyBookings, eq.pricePerDay, data.pricePerDay)}
                          payout={allowDailyBookings ? equipmentDayPayoutDisplay(allowDailyBookings, effectiveDay, feePercentage) : null}
                        />
                      </TableCell>
                      <TableCell>
                        <PriceWithPayout
                          price={equipmentMonthPriceDisplay(eq.pricePerMonth, data.pricePerMonth)}
                          payout={estimatePayout(effectiveMonth, feePercentage)}
                        />
                      </TableCell>
                      <TableCell>
                        <PriceWithPayout
                          price={equipmentOverageRateDisplay(eq.overageRate, defaultOverage)}
                          payout={estimatePayout(effectiveOverage, feePercentage)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            onClick={() => openEditModal(index)}
                            aria-label={
                              eq.equipmentType
                                ? `Edit ${eq.equipmentType}`
                                : 'Edit equipment type'
                            }
                            title="Edit equipment type"
                            size="small"
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => removeEquipmentRowAt(index)}
                            aria-label={
                              eq.equipmentType
                                ? `Remove ${eq.equipmentType}`
                                : 'Remove equipment type'
                            }
                            title="Remove equipment type"
                            size="small"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={openAddModal}
          disabled={availableToAdd.length === 0}
        >
          Add Equipment
        </Button>

        {/* Equipment Add/Edit Modal */}
        <Dialog
          open={modal.open}
          onClose={handleModalClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {modal.editIndex !== null ? 'Edit Equipment' : 'Add Equipment'}
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
            <TextField
              select
              label="Equipment Type"
              required
              fullWidth
              value={modal.equipmentType}
              onChange={(e) => setModal((prev) => ({ ...prev, equipmentType: e.target.value }))}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>Select equipment type…</em>
              </MenuItem>
              {modalAvailableTypes.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            {allowDailyBookings && (
              <TextField
                label="Price Per Day (optional)"
                fullWidth
                value={modal.pricePerDay}
                onChange={(e) => handleModalCurrencyChange('pricePerDay', e.target.value)}
                placeholder={defaultPlaceholder(data.pricePerDay)}
                error={Boolean(modalDayError)}
                helperText={modalDayError || 'Leave blank to use your default daily rate.'}
              />
            )}
            <TextField
              label="Price Per Month (optional)"
              fullWidth
              value={modal.pricePerMonth}
              onChange={(e) => handleModalCurrencyChange('pricePerMonth', e.target.value)}
              placeholder={defaultPlaceholder(data.pricePerMonth)}
              error={Boolean(modalMonthError)}
              helperText={modalMonthError || 'Leave blank to use your default monthly rate.'}
            />
            <TextField
              label="Overage Rate (optional)"
              fullWidth
              value={modal.overageRate}
              onChange={(e) => handleModalCurrencyChange('overageRate', e.target.value)}
              placeholder={`${formatDisplay(
                defaultEquipmentOverageRate(modalPrices, data)
              )} (default)`}
              error={Boolean(modalOverageError)}
              helperText={modalOverageError || 'Leave blank to use your default overage rate.'}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleModalClose} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleModalSave}
              disabled={!modal.equipmentType || hasModalError}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default LocationPricingStep;
